import { Button } from "@/components/Button/Button";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { useOrg } from "@/hooks/org";
import { useUser } from "@/hooks/user";
import {
  useDeleteUserApiV1UsersUserIdDeleteMutation,
  useResetUserApiV1UsersUserIdResetPutMutation,
  useUpdateUserApiV1UsersUserIdPatchMutation
} from "@/store/clientApi";
import { ensureError, localizeError } from "@/utils/helpers";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Divider, FormControl, LinearProgress, OutlinedInput, Theme, Typography, useFormControl } from "@mui/material";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface UserDetailsProps {
  close?: () => void;
}

const UserDetails: FC<UserDetailsProps> = (props) => {
  const { t } = useTranslation("users");

  const { logout } = useAuth0();

  const [name, setName] = useState<string | undefined>();
  const [resetDisabled, setResetDisabled] = useState<boolean>(false);
  const [openDeleteWarning, setOpenDeleteWarning] = useState<boolean>(false);

  const { userId, data: userData, error, isLoading } = useUser();
  if (userData?.name && name === undefined) {
    setName(userData?.name);
  }

  const { org } = useOrg();

  const [updateUser] = useUpdateUserApiV1UsersUserIdPatchMutation();
  const [deleteSelf, { isSuccess: deletedSelf }] = useDeleteUserApiV1UsersUserIdDeleteMutation({});
  const [reset] = useResetUserApiV1UsersUserIdResetPutMutation();

  if (deletedSelf) {
    logout({
      logoutParams: { returnTo: window.location.origin }
    });
  }

  const NameUpdater = () => {
    const { focused } = useFormControl() || {};
    const [lastState, setLastState] = useState<boolean>(false);

    useEffect(() => {
      const updateName = async () => {
        if (!userId || name === userData?.name || name === "") {
          return;
        }
        try {
          await updateUser({
            userId: encodeURI(userId),
            updateUserRequest: { name: name }
          }).unwrap();
        } catch (err) {
          const error = ensureError(err);
          toast.error(localizeError(t, error));
        }
      };
      if (lastState !== focused) {
        if (focused === false) {
          updateName();
        }
        setLastState(focused ?? false);
      }
    }, [focused, setLastState, lastState]);

    return <>{null}</>;
  };

  const resetPassword = async () => {
    if (!userId) {
      return;
    }

    setResetDisabled(true);

    try {
      await reset({
        userId: encodeURI(userId)
      }).unwrap();
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));

      setResetDisabled(false);

      return;
    }

    setTimeout(() => setResetDisabled(false), 1000);
  };

  if (error) {
    return <>{localizeError(t, ensureError(error))}</>;
  } else if (isLoading) {
    return <LinearProgress />;
  } else if (!userData) {
    return <></>;
  }

  return (
    <>
      <Box
        component="div"
        sx={{
          width: "552px",
          maxWidth: "100%",
          minHeight: "min(40vh, 617px)",
          "& div": { marginBottom: "0.5em" },
          "& hr": { mb: "0.5em", marginTop: "1em" },
          "& div .MuiTypography-h2": { marginBottom: "0.7em" }
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" }
          }}
        >
          <Typography sx={{ margin: "16px 0px" }}>{t("common:title.name")}</Typography>
          <FormControl sx={{ width: "25ch", "&.MuiFormControl-root": { mb: "0px", "& .MuiOutlinedInput-root": { mb: "0px" } } }}>
            <OutlinedInput id="display-name" size="small" value={name} error={name === ""} onChange={(e) => setName(e.target.value)} />
            <NameUpdater />
          </FormControl>
        </Box>
        <Divider />
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0, sm: "1.5em" }
          }}
        >
          <Box component="div">
            <Typography sx={{ margin: "16px 0px" }}>{t("title.changePassword")}</Typography>
            <Typography
              sx={{
                color: (theme: Theme) => theme.palette.text.secondary
              }}
            >
              {t(
                "message.changePassword",
                userData?.email
                  ? {
                      context: "email",
                      email: userData?.email
                    }
                  : undefined
              )}
            </Typography>
          </Box>
          <Button
            id="change-password"
            variant="contained"
            onClick={() => {
              if (userId) {
                resetPassword();
              }
            }}
            disabled={resetDisabled}
            sx={{ flexShrink: 0 }}
          >
            {resetDisabled ? t("message.linkSent") : t("button.changePassword")}
          </Button>
        </Box>
        <Divider />
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0, sm: "1.5em" }
          }}
        >
          <Box component="div">
            <Typography sx={{ margin: "16px 0px" }}>{t("title.deleteProfile")}</Typography>
            <Typography
              sx={{
                color: (theme: Theme) => theme.palette.text.secondary
              }}
            >
              {t(
                "message.deleteProfile",
                org?.name
                  ? {
                      context: "org",
                      org: org?.name
                    }
                  : undefined
              )}
            </Typography>
          </Box>
          <Button id="delete-profile" variant="contained" color="error" onClick={() => setOpenDeleteWarning(true)} sx={{ flexShrink: 0 }}>
            {t("button.deleteProfile")}
          </Button>
        </Box>
      </Box>
      <DialogBox
        title={t("title.confirmDeleteProfile")}
        message={t(
          "message.deleteProfile",
          org?.name
            ? {
                context: "org",
                org: org?.name
              }
            : undefined
        )}
        open={openDeleteWarning}
        setOpen={setOpenDeleteWarning}
        args={undefined}
        onClose={(approve) => {
          if (approve && userId) {
            const remove = async () => {
              try {
                await deleteSelf({
                  userId: encodeURI(userId)
                }).unwrap();
                if (props.close) {
                  props.close();
                }
              } catch (err) {
                const error = ensureError(err);
                toast.error(localizeError(t, error));
              }
            };
            remove();
          }
        }}
        confirmColor="error"
        confirmText={t("button.deleteProfile")}
        declineText={t("common:button.cancel")}
      />
    </>
  );
};

export { UserDetails };
